// ======================================================================
// File: RFQList.jsx
// Module: PPE → Procurement → RFQ
//
// Purpose:
// - Display RFQ list dashboard
// - Summary cards
// - Table view
// - ACTIONS WIRED: View, Send, Compare
// ======================================================================
import { formatDate } from "../../../../../../utils/formatUtils";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import useRFQList from "../hooks/useRFQList";

import RFQFormDrawer from "../form/RFQFormDrawer";
import SendRFQDrawer from "../form/SendRFQDrawer";
import RFQViewDrawer from "./RFQViewDrawer";

import "../rfq-ui.css";

export default function RFQList() {

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const {
    loading,
    records,
    summary,
    reload
  } = useRFQList({
    search,
    status,
    from_date: fromDate,
    to_date: toDate
  });

  const [showCreate, setShowCreate] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showView, setShowView] = useState(false);

  const [selectedRFQ, setSelectedRFQ] = useState(null);
  
  // ==============================================================
  // HANDLERS
  // ==============================================================

  const handleOpenSend = (rfqId) => {
    setSelectedRFQ(rfqId);
    setShowSend(true);
  };

  const handleCompare = (rfqId) => {
    navigate(`/ppe/procurement/rfq/compare/${rfqId}`);
  };

  const handleView = (rfqId) => {
    setSelectedRFQ(rfqId);
    setShowView(true);
  };

  return (
    <div>

      {/* HEADER CARD */}
      <div className="rfq-header-card">
        <h2>RFQ Management</h2>
        <p>Request for Quotation Management</p>
      </div>

      {/* SUMMARY */}
      <div className="rfq-summary">

        <div className="rfq-summary-card">
          <h4>{summary.total || records.length}</h4>
          <div>Total RFQs</div>
        </div>

        <div className="rfq-summary-card">
          <h4>{summary.sent || 0}</h4>
          <div>Sent to Vendors</div>
        </div>

        <div className="rfq-summary-card">
          <h4>{summary.responded || 0}</h4>
          <div>Responses Received</div>
        </div>

        <div className="rfq-summary-card">
          <h4>{summary.finalized || 0}</h4>
          <div>Finalized</div>
        </div>

      </div>

      {/* ACTION BAR */}
      <div className="rfq-filters">

        <input
          type="text"
          placeholder="Search RFQ No / PR No"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rfq-filter-input"
        />

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rfq-filter-select"
        >
          <option value="">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="SENT">Sent</option>
          <option value="PARTIALLY_RESPONDED">Partially Responded</option>
          <option value="RESPONDED">Responded</option>
          <option value="FINALIZED">Finalized</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rfq-filter-date"
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="rfq-filter-date"
        />

        <button
          className="btn-secondary"
          onClick={() =>
            reload({
              search,
              status,
              from_date: fromDate,
              to_date: toDate
            })
          }
        >
          Search
        </button>

        <button
          className="btn-primary"
          onClick={() => setShowCreate(true)}
        >
          + Create RFQ
        </button>

        <button
          className="btn-secondary"
          onClick={() => reload()}
        >
          Refresh
        </button>

      </div>

      {/* TABLE */}
      <div className="rfq-table-wrapper">

        {loading && (
          <div style={{ padding: "10px" }}>
            Loading RFQs...
          </div>
        )}

        {!loading && (
          <table className="rfq-table">

            <thead>
              <tr>
                <th>RFQ No</th>
                <th>Date</th>
                <th>PR Ref</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {records.length === 0 && (
                <tr>
                  <td colSpan="6">
                    No RFQs available
                  </td>
                </tr>
              )}

              {records.map((rfq) => (
                <tr key={rfq.id}>

                  <td>{rfq.rfq_no}</td>

                  <td>{formatDate(rfq.rfq_date)}</td>

                  <td>{rfq.pr_no || rfq.pr_id}</td>

                  <td>
                    {rfq.quote_due_date
                      ? formatDate(rfq.quote_due_date)
                      : "-"}
                  </td>

                  <td>
                    <span
                      className={`rfq-status-badge rfq-status-${(
                        rfq.status_code ||
                        rfq.status_name ||
                        "draft"
                      )
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      {rfq.status_name ||
                        rfq.status_code ||
                        "N/A"}
                    </span>
                  </td>

                  <td>
                    <div className="rfq-actions">

                      <button
                        className="rfq-btn-view"
                        onClick={() => handleView(rfq.id)}
                      >
                        View
                      </button>

                      <button
                        className="rfq-btn-send"
                        onClick={() => handleOpenSend(rfq.id)}
                      >
                        Send
                      </button>

                      <button
                        className="rfq-btn-compare"
                        onClick={() => handleCompare(rfq.id)}
                      >
                        Compare
                      </button>

                    </div>
                  </td>

                </tr>
              ))}

            </tbody>
          </table>
        )}

      </div>

      {/* CREATE RFQ DRAWER */}
      {showCreate && (
        <RFQFormDrawer
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            reload();
          }}
        />
      )}

      {/* SEND RFQ DRAWER */}
      {showSend && (
        <SendRFQDrawer
          open={showSend}
          rfqId={selectedRFQ}
          onClose={() => setShowSend(false)}
          onSaved={() => {
            setShowSend(false);
            reload();
          }}
        />
      )}

      {/* VIEW RFQ DRAWER */}
      {showView && (
        <RFQViewDrawer
          open={showView}
          rfqId={selectedRFQ}
          onClose={() => setShowView(false)}
        />
      )}

    </div>
  );
}
